// whocare: timeline card of a long-form article. Colours only from the stock theme variables, so it follows the
// light / dark / high-contrast themes and the whocare "Kiwi black" theme (which only redefines those variables).

.card {
  container-type: inline-size;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  box-sizing: border-box;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  text-decoration: none;
  overflow: hidden;
  transition: background-color 0.15s ease;

  &:hover,
  &:focus-visible {
    background: var(--color-bg-secondary);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-text-brand);
    outline-offset: 2px;
  }
}

// not a link (inside a clickable notification row or a link): the row gives the hover feedback and the cursor
.static {
  cursor: inherit;

  &:hover {
    background: var(--color-bg-primary);
  }
}

.text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 2px 8px 2px 6px;
  margin-bottom: 2px;
  border-radius: 999px;
  background: var(--color-bg-brand-softest);
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
}

.title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--color-text-primary);
  overflow-wrap: anywhere;
}

.subtitle {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  font-size: 15px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  overflow-wrap: anywhere;
}

// three lines, the last one fading out (the excerpt is ~200 characters and nearly always overflows)
.excerpt {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  overflow-wrap: anywhere;
  mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - 1.6em),
    rgb(0 0 0 / 35%) 100%
  );
}

.readMore {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-brand);

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }

  .card:hover & {
    text-decoration: underline;
  }
}

.cover {
  position: relative;
  flex: 0 0 auto;
  width: 104px;
  height: 104px;
  display: block;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-bg-secondary);
}

.coverBlurhash,
.coverImage {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.coverImage {
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.coverImageLoaded {
  opacity: 1;
}

// quotes and summaries: title (two lines at most), one line of excerpt, small thumbnail
.compact {
  gap: 10px;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;

  .text {
    gap: 2px;
  }

  .pill {
    margin-bottom: 0;
    padding: 0 6px 0 4px;
    font-size: 11px;
    line-height: 16px;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  .title {
    font-size: 15px;
    line-height: 1.35;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .excerpt {
    display: block;
    font-size: 13px;
    line-height: 1.5;
    white-space: nowrap;
    text-overflow: ellipsis;
    mask-image: none;
  }

  .cover {
    width: 48px;
    height: 48px;
    border-radius: 6px;
  }
}

// narrow columns (advanced UI, phones): smaller thumbnail so the text keeps most of the width
@container (max-width: 360px) {
  .cover {
    width: 72px;
    height: 72px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .coverImage {
    transition: none;
  }
}
